import { GameHowl } from "./GameHowl";

export class TempoDetector {
    private THRESHOLD: number = 0.98;

    public detectTempo(howl: GameHowl): Promise<number>;
    public detectTempo(buffer: AudioBuffer): Promise<number>;
    public detectTempo(src: GameHowl | AudioBuffer): Promise<number> {
        return new Promise<number>(
            (resolve) => {
                let buffer: AudioBuffer;
                if (src instanceof GameHowl) {
                    buffer = src.buffer;
                } else {
                    buffer = src;
                }

                const offlineContext = new OfflineAudioContext(
                    1,
                    buffer.length,
                    buffer.sampleRate
                );
                const source = offlineContext.createBufferSource();
                source.buffer = buffer;
                const filter = offlineContext.createBiquadFilter();
                filter.type = "lowpass";
                // filter.frequency.value = 1000;
                source.connect(filter);
                filter.connect(offlineContext.destination);
                source.start(0);
                offlineContext.startRendering();
                offlineContext.oncomplete = (
                    e: OfflineAudioCompletionEvent
                ) => {
                    const result = this.process(e);
                    resolve(result);
                };
            }
        );
    }

    private process(e: OfflineAudioCompletionEvent): number {
        // If you want to analyze both channels, use the other channel later
        const data = e.renderedBuffer.getChannelData(0);
        const max = this.arrayMax(data);
        const min = this.arrayMin(data);
        const threshold = min + (max - min) * this.THRESHOLD;
        const peaks = this.getPeaksAtThreshold(data, threshold);
        const intervalCounts = this.countIntervalsBetweenNearbyPeaks(peaks);
        const tempoCounts = this.groupNeighborsByTempo(intervalCounts);
        tempoCounts.sort((a: TempoCount, b: TempoCount) => {
            return b.count - a.count;
        });

        return tempoCounts[0]?.tempo || 0;
    }

    private getPeaksAtThreshold(data: Float32Array, threshold: number): number[] {
        const peaksArray: number[] = [];
        const length = data.length;
        for (let i = 0; i < length; ) {
            if (data[i] > threshold) {
                peaksArray.push(i);
                // Skip forward ~ 1/4s to get past this peak.
                i += 10000;
            }

            i++;
        }

        return peaksArray;
    }

    private countIntervalsBetweenNearbyPeaks(peaks: number[]): IntervalCount[] {
        const intervalCounts: IntervalCount[] = [];
        peaks.forEach((peak, index) => {
            for (let i = 0; i < 10; i++) {
                const interval = peaks[index + i] - peak;
                const foundInterval = intervalCounts.some((intervalCount) => {
                    if (intervalCount.interval === interval) {
                        return intervalCount.count++;
                    }
                });

                // Additional checks to avoid infinite loops in later processing
                if (!isNaN(interval) && interval !== 0 && !foundInterval) {
                    intervalCounts.push({
                        interval,
                        count: 1
                    });
                }
            }
        });

        return intervalCounts;
    }

    private groupNeighborsByTempo(intervalCounts: IntervalCount[]): TempoCount[] {
        const tempoCounts: TempoCount[] = [];
        intervalCounts.forEach((intervalCount) => {
            // Convert an interval to tempo
            let theoreticalTempo = 60 / (intervalCount.interval / 44100);
            theoreticalTempo = Math.round(theoreticalTempo);
            if (theoreticalTempo === 0) {
                return;
            }

            // Adjust the tempo to fit within the 90-180 BPM range
            while (theoreticalTempo < 90) {
                theoreticalTempo *= 2;
            }

            while (theoreticalTempo > 180) {
                theoreticalTempo /= 2;
            }

            const foundTempo = tempoCounts.some((tempoCount) => {
                if (tempoCount.tempo === theoreticalTempo)
                    return (tempoCount.count += intervalCount.count);
            });

            if (!foundTempo) {
                tempoCounts.push({
                    tempo: theoreticalTempo,
                    count: intervalCount.count,
                });
            }
        });

        return tempoCounts;
    }

    private arrayMin(arr: Float32Array): number {
        let len = arr.length;
        let min = Infinity;

        while (len--) {
            if (arr[len] < min) {
                min = arr[len];
            }
        }

        return min;
    }

    private arrayMax(arr: Float32Array): number {
        let len = arr.length;
        let max = -Infinity;

        while (len--) {
            if (arr[len] > max) {
                max = arr[len];
            }
        }

        return max;
    }
}

class TempoCount {
    tempo: number;
    count: number;
}

class IntervalCount {
    interval: number;
    count: number;
}

export default new TempoDetector();
